import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { formatDay } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { MoneyInputError, formatWireForDisplay, parseMajorInput } from '../money.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { sentence } from '../sentence.js'
import { DataSurface } from './DataSurface.js'

/**
 * The repairs on the bench. §6.27 `REP-001` to `REP-004`; §15.13.
 *
 * **A free adjustment is a repair, and recording it is the requirement.** `REP-004`: "Adjustments
 * are recorded even when free, they are the main reason customers return and the main source of
 * goodwill, and an unrecorded adjustment is an invisible cost." So a zero charge is a real answer
 * this screen offers rather than an empty field somebody skips, and the charge is required at
 * classification: blank and zero are different things and only one of them is a decision.
 *
 * **`stage` is derived, not stored.** Appendix E registers no repair status, so the three stages
 * come from two timestamps and the screen shows what the server derived rather than deriving its
 * own. The buttons follow the stage: a received repair is classified, a classified one is
 * completed, and a completed one is history.
 *
 * **An absent shop cost is a permission, not a free repair.** The service builds the figure and
 * removes it at the response boundary per `ROLE-001a`'s per-branch grant, so the screen says
 * nothing about cost rather than showing a blank that reads as zero. A zero customer charge, by
 * contrast, is printed, because that is `REP-004`'s free adjustment.
 *
 * **`soldByThisShop` changes the conversation and so it is on the row.** `REP-002` makes a repair
 * on a frame the shop did not sell a liability question, which is why intake takes a policy version
 * at all; a counter deciding what to promise needs to know which kind it is looking at.
 */

export interface RepairData {
  readonly id: string
  readonly customerId: string
  /** `received`, `classified` or `completed`, derived by the server from two timestamps. */
  readonly stage: string
  readonly condition: string
  readonly promisedDate: string
  readonly soldByThisShop: boolean
  readonly version: number
  readonly parts: readonly { readonly sku: string; readonly quantity: number }[]
  /** Minor units. `'0'` is `REP-004`'s free adjustment and is printed. */
  readonly chargeMinor?: string
  readonly currency?: string
  /** Absent means Appendix B's cost row is withheld from this reader. */
  readonly shopCostMinor?: string
  readonly outcomeDescription?: string
  readonly warrantyComponent?: string
}

export interface RepairsProps {
  /**
   * The branch's timezone, for `LOC-004`.
   *
   * Appendix J: an instant renders in the branch's zone, never the browser's. A plain `date`
   * column renders in none, which is what `formatDay` is for.
   */
  readonly timezone: string
  readonly load: (stage?: string) => Promise<readonly RepairData[]>
  /** The shop's own currency, for the charge field. Never guessed here. */
  readonly currency: string
  readonly onOpenCustomer?: (customerId: string) => void
}

export function Repairs({ load, currency, onOpenCustomer, timezone }: RepairsProps): JSX.Element {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [stage, setStage] = useState('')
  const [rows, setRows] = useState<readonly RepairData[] | null>(null)
  const [charges, setCharges] = useState<Record<string, string>>({})
  const [outcomes, setOutcomes] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  // The list read's failure, apart from `error`, which carries form validation: typing a bad charge
  // must not replace the whole list with an error.
  const [loadError, setLoadError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setRows(await load(stage === '' ? undefined : stage))
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught instanceof Error ? caught.message : t('repairs.loadFailed'))
    }
  }, [load, stage, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    void refresh()
  })

  const classify = useCallback(
    async (row: RepairData) => {
      const typed = charges[row.id]
      if (typed === undefined || typed.trim() === '') {
        // `REP-004`: blank and zero are different answers, and only zero is a decision. A blank
        // field classified as free would make every skipped repair look like goodwill.
        setError(t('repairs.chargeRequired'))
        return
      }
      let chargeMinor: string
      try {
        chargeMinor = parseMajorInput(typed, currency).toString()
      } catch (parseError) {
        setError(
          parseError instanceof MoneyInputError ? parseError.message : t('repairs.chargeBad'),
        )
        return
      }
      setError(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/repairs/${row.id}/classification`,
        label: t('repairs.queueClassifyLabel'),
        body: { expectedVersion: row.version, customerChargeMinor: chargeMinor },
      })) as { id: string }
      setWriteId(write.id)
    },
    [charges, currency, enqueue, t],
  )

  const complete = useCallback(
    async (row: RepairData) => {
      const description = outcomes[row.id]
      if (description === undefined || description.trim() === '') {
        // `REP-001` tracks an outcome, and the server refuses a blank one with exactly this
        // reasoning: it records that something happened and not what.
        setError(t('repairs.outcomeRequired'))
        return
      }
      setError(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/repairs/${row.id}/outcome`,
        label: t('repairs.queueCompleteLabel'),
        body: { expectedVersion: row.version, description: description.trim() },
      })) as { id: string }
      setWriteId(write.id)
    },
    [outcomes, enqueue, t],
  )

  const line = statusLine(t, snapshot, writeId, t('queue.subject.repair'))

  return (
    <section className="lm-screen" aria-labelledby="repairs-heading">
      <h1 id="repairs-heading" className="lm-screen__heading">
        {t('repairs.heading')}
      </h1>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('repairs.stageLabel')}</span>
        <select
          className="lm-input"
          value={stage}
          onChange={(e) => {
            setStage(e.currentTarget.value)
          }}
        >
          <option value="">{t('repairs.stageOpen')}</option>
          <option value="received">{t('repairs.stageReceived')}</option>
          <option value="classified">{t('repairs.stageClassified')}</option>
          <option value="completed">{t('repairs.stageCompleted')}</option>
        </select>
      </label>

      {error === null ? null : (
        <p className="lm-hint" role="alert">
          {error}
        </p>
      )}

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      {/*
        The stage select stays outside so a shopkeeper can widen the filter from the filtered state.
        The resting stage ('') is the server's open set, so nothing on the bench is first-run and any
        chosen stage, Finished included, is a filter that matched nothing.
      */}
      <DataSurface
        state={
          loadError !== null && rows === null
            ? { kind: 'error', message: loadError, onRetry: () => void refresh() }
            : rows === null
              ? { kind: 'loading' }
              : rows.length === 0
                ? { kind: stage === '' ? 'empty' : 'filtered' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={3}
        what={t('repairs.what')}
        emptyMessage={t('repairs.empty')}
        filteredMessage={t('repairs.filtered')}
      >
        <ul className="lm-repair-list">
          {(rows ?? []).map((row) => (
            <li key={row.id} className="lm-repair">
              <span className="lm-repair__condition">{row.condition}</span>
              <span className="lm-repair__promised">
                {sentence(t, 'repairs.promised', {
                  date: <Ltr>{formatDay(row.promisedDate, timezone, locale)}</Ltr>,
                })}
              </span>
              {row.soldByThisShop ? null : (
                // `REP-002`: a frame the shop did not sell is a liability question, which is why
                // intake takes a policy version. A counter promising a date needs to know.
                <span className="lm-repair__not-ours">{t('repairs.notOurs')}</span>
              )}

              {row.chargeMinor === undefined || row.currency === undefined ? null : (
                <span className="lm-repair__charge" dir="ltr">
                  {/* Zero is printed, because `REP-004` makes a free adjustment a real answer and
                      a blank would hide the goodwill the requirement is about. */}
                  {row.chargeMinor === '0'
                    ? t('repairs.free')
                    : t('repairs.charge', {
                        amount: formatWireForDisplay(row.chargeMinor, row.currency),
                      })}
                </span>
              )}

              {row.shopCostMinor === undefined || row.currency === undefined ? null : (
                <span className="lm-repair__cost" dir="ltr">
                  {t('repairs.shopCost', {
                    amount: formatWireForDisplay(row.shopCostMinor, row.currency),
                  })}
                </span>
              )}

              {row.parts.length === 0 ? null : (
                <span className="lm-repair__parts">
                  {t('repairs.parts', {
                    parts: row.parts.map((p) => `${p.sku} x${String(p.quantity)}`).join(', '),
                  })}
                </span>
              )}

              {row.outcomeDescription === undefined ? null : (
                <span className="lm-repair__outcome">{row.outcomeDescription}</span>
              )}

              {onOpenCustomer === undefined ? null : (
                <button
                  type="button"
                  className="lm-button lm-button--secondary"
                  onClick={() => {
                    onOpenCustomer(row.customerId)
                  }}
                >
                  {t('repairs.openCustomer')}
                </button>
              )}

              {row.stage === 'received' ? (
                <>
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">
                      {t('repairs.chargeLabel', { currency })}
                    </span>
                    <input
                      className="lm-input"
                      inputMode="decimal"
                      autoComplete="off"
                      value={charges[row.id] ?? ''}
                      onChange={(e) => {
                        const value = e.currentTarget.value
                        setCharges((previous) => ({ ...previous, [row.id]: value }))
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    className="lm-button lm-button--primary"
                    onClick={() => void classify(row)}
                  >
                    {t('repairs.classify')}
                  </button>
                </>
              ) : null}

              {row.stage === 'classified' ? (
                <>
                  <label className="lm-field lm-field--inline">
                    <span className="lm-field__label">{t('repairs.outcomeLabel')}</span>
                    <input
                      className="lm-input"
                      autoComplete="off"
                      value={outcomes[row.id] ?? ''}
                      onChange={(e) => {
                        const value = e.currentTarget.value
                        setOutcomes((previous) => ({ ...previous, [row.id]: value }))
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    className="lm-button lm-button--primary"
                    onClick={() => void complete(row)}
                  >
                    {t('repairs.complete')}
                  </button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      </DataSurface>
    </section>
  )
}
